/**
 * Routing: every screen is a URL.
 *
 * Both applications used to keep the current screen in `useState`. That renders the right thing and
 * is not navigation — the address bar never changed, so nothing could be linked to, the browser's
 * back button left the application entirely, a refresh dropped an employee back on the first screen,
 * and "open in a new tab" on a nav item was impossible. In a tool people keep open all day beside
 * three others, that is a daily annoyance rather than a theoretical one.
 *
 * ## Why this is written here rather than installed
 *
 * `react-router` is the obvious answer and would be the right one if this were a routing problem.
 * It is not: each application has three or four flat destinations, no nested layouts, no route
 * parameters and no data loading in the router. What is actually needed is the History API plus the
 * accessibility work that a `useState` swap would skip, and that is the file below.
 *
 * The codebase already argues this for icons — a package whose surface is ninety-nine per cent
 * unused is one more thing for a bank to audit. The same argument holds here, and it stops holding
 * the moment routes need parameters, nesting or guards with data behind them. `docs/adr/0006`
 * records that, so whoever hits that wall knows it was anticipated rather than overlooked.
 *
 * ## What it gets right that a naive version does not
 *
 * **Navigation is an anchor.** Nav items render `<a href>`, so middle-click, cmd-click, "open in new
 * tab" and the browser's own link handling work, and a screen reader announces a link rather than a
 * button that mysteriously changes the page. Plain left-clicks are intercepted; everything else is
 * left to the browser, which is the only way those gestures keep working.
 *
 * **Focus moves.** A single-page navigation changes the whole main region while focus stays on the
 * link that was clicked. A screen-reader user is given no indication anything happened, and a
 * keyboard user's next Tab resumes inside the rail rather than in the new page. On every route
 * change focus moves to the page heading, which is also what announces the new screen.
 *
 * **The server has to agree.** A URL only works if a request for it returns the application. Vite's
 * dev server does that by default; `deploy/docker/frontend.nginx.conf` needs `try_files` for the
 * built bundle, and does have it.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";

export interface RouterValue {
  /** Current path, always with a leading slash and no trailing one (except the root). */
  path: string;
  /** Go to a path. `replace` rewrites history instead of adding to it. */
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

/** Normalised so `/chat`, `/chat/` and `chat` are one route rather than three. */
function normalise(path: string): string {
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  const trimmed = withSlash.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

export function RouterProvider({ children }: { children: ReactNode }): JSX.Element {
  const [path, setPath] = useState(() => normalise(window.location.pathname));

  // `popstate` covers the back and forward buttons. It does not fire for `pushState`, which is why
  // `navigate` sets the state itself rather than relying on an event.
  useEffect(() => {
    const onPop = (): void => setPath(normalise(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    const target = normalise(to);
    if (target === normalise(window.location.pathname)) return;
    if (options?.replace) {
      window.history.replaceState(null, "", target);
    } else {
      window.history.pushState(null, "", target);
    }
    setPath(target);
  }, []);

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) {
    throw new Error("useRouter must be used inside a RouterProvider");
  }
  return value;
}

/**
 * Resolve the current path against a set of routes, redirecting when it matches none.
 *
 * Returns the matched id. The redirect uses `replace`, so an employee who lands on a URL that no
 * longer exists — a bookmark from before a screen was renamed, or a page their role cannot reach —
 * does not have to press back twice to escape a route they never chose to visit.
 */
export function useRoute<Id extends string>(
  routes: readonly { id: Id; path: string }[],
  fallback: Id,
): Id {
  const { path, navigate } = useRouter();
  const match = routes.find((route) => normalise(route.path) === path);
  const fallbackRoute = routes.find((route) => route.id === fallback) ?? routes[0];

  useEffect(() => {
    if (!match && fallbackRoute) {
      navigate(fallbackRoute.path, { replace: true });
    }
  }, [match, fallbackRoute, navigate]);

  return (match?.id ?? fallback) as Id;
}

export interface RouteLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  children: ReactNode;
}

/**
 * An anchor that navigates without a reload.
 *
 * A real `href` is the point. It is what makes the destination visible in the status bar, what lets
 * the browser open it in a new tab, and what makes a screen reader call it a link. Only the plain
 * left-click is taken; a modified click, a middle click or anything with a target is handed back to
 * the browser, because those gestures mean "not here" and intercepting them breaks them silently.
 */
export function RouteLink({ to, children, onClick, ...rest }: RouteLinkProps): JSX.Element {
  const { navigate } = useRouter();

  return (
    <a
      href={to}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        const modified =
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
        if (modified || rest.target) return;
        event.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
